import { useState } from "react";
import { useParams } from "react-router-dom";
import { JournalDetailsHeader } from "@/components/catalog/JournalDetailsHeader";
import { JournalHoldingsTree } from "@/components/catalog/JournalHoldingsTree";
import { JournalLegacyCopyList } from "@/components/catalog/JournalLegacyCopyList";
import { LegacyJournalHoldingsNote } from "@/components/catalog/LegacyJournalHoldingsNote";
import { ReceiveIssueForm } from "@/components/catalog/ReceiveIssueForm";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useJournalDetails } from "./useJournalDetails";

const RECEIVE_PERMISSIONS = [
  "catalog.create",
  "catalog.edit",
  "catalog.*",
  "book.create",
  "book.*",
];

export function JournalDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const { hasAnyPermission } = useAccessControl();
  const { biblio, holdings, loading, error, refreshHoldings } = useJournalDetails(id);
  const [receiving, setReceiving] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const volumes = holdings?.volumes ?? [];
  const isStructured = volumes.length > 0;
  const isLegacy = !isStructured && Boolean(biblio?.Items?.length);
  const canReceive = !isLegacy && hasAnyPermission(RECEIVE_PERMISSIONS);
  const issueCount = volumes.reduce((total, volume) => total + volume.issueCount, 0);
  const copyCount = volumes.length
    ? volumes.reduce((total, volume) => total + volume.copyCount, 0)
    : (biblio?.Items?.length ?? 0);

  const completeRefresh = async () => {
    try {
      await refreshHoldings();
      setRefreshError(null);
      setReceiving(false);
    } catch {
      setRefreshError(
        "The issue was received, but holdings could not be refreshed. Please try again."
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadingOverlay size="lg" label="Loading journal details..." />
      </div>
    );
  }

  if (error || !biblio || !holdings) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p role="alert">{error ?? "Journal details are unavailable."}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Journal Details"
        description="View bibliographic information and journal holdings"
        breadcrumbItems={[{ label: "Catalog", href: "/catalog" }, { label: biblio.title }]}
        showSearch={false}
      />
      <JournalDetailsHeader
        biblio={biblio}
        volumeCount={volumes.length}
        issueCount={issueCount}
        copyCount={copyCount}
      />
      {canReceive ? (
        <div className="flex justify-end">
          <Button
            type="button"
            aria-expanded={receiving}
            onClick={() => {
              setRefreshError(null);
              setReceiving((open) => !open);
            }}
          >
            {receiving ? "Cancel receiving" : "Receive Issue"}
          </Button>
        </div>
      ) : null}
      {canReceive && receiving ? (
        <section aria-label="Receive journal issue">
          {refreshError ? (
            <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 text-destructive">
              <p className="text-sm">{refreshError}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void completeRefresh()}
              >
                Retry refresh
              </Button>
            </div>
          ) : null}
          <ReceiveIssueForm biblioId={biblio.id} onSuccess={completeRefresh} />
        </section>
      ) : null}
      <section className="space-y-4" aria-labelledby="journal-holdings-heading">
        <h2 id="journal-holdings-heading" className="text-xl font-semibold">
          Holdings
        </h2>
        {isStructured ? (
          <JournalHoldingsTree volumes={volumes} />
        ) : isLegacy ? (
          <>
            <LegacyJournalHoldingsNote />
            <JournalLegacyCopyList items={biblio.Items ?? []} />
          </>
        ) : (
          <JournalHoldingsTree volumes={[]} />
        )}
      </section>
    </div>
  );
}
